/**
 * The seam every provider adapter is written against.
 *
 * Separate from `transport.ts` specifically because `ModelError` and `matchSeen`
 * are VALUES — adapters throw the first and `reason.ts` instanceof-checks it, and
 * both adapters call the second. `transport.ts` dynamically imports the adapters,
 * so a value living there would either be a real runtime cycle or would drag an
 * adapter into the initial bundle, which is the whole thing the dynamic import
 * buys.
 *
 * What is in here is what the app needs from a model, said once. What is NOT in
 * here is anything only one provider can do: this file is the reason the six
 * epistemic features have exactly one provider-shaped branch between them, and
 * it is `SearchOutcome.pageText`.
 */

import type { ModelStamp, Spend, SpendDetail } from '../../core/types'
import type { Effort, ResolvedModel } from '../../core/models'
import { canonicalUrl } from '../../core/url'
import { cspRefused, cspRefusedSentence } from './cspRefused'

/**
 * A schema with the name one API requires and the other has no field for.
 *
 * The name belongs to the schema rather than to the call: it is stable per
 * schema and it lands in the model's context, so it is written as prose a model
 * can read rather than derived from the schema's single top-level key — a derived
 * name changes silently the moment someone renames that key for an unrelated
 * reason.
 */
export interface SchemaSpec {
  readonly name: string
  readonly schema: unknown
}

/** Server-side search for one call. */
export interface WebOptions {
  /**
   * Searches this pass may run.
   *
   * A bound where the API enforces one and a request where it does not. Anthropic
   * takes it as `max_uses`. OpenAI's `web_search` has NO `max_uses` — the loop is
   * internal — so there it sizes `search_context_size` instead and the count
   * itself reaches the model as a sentence in `buildVerifySystem`. It lives here
   * rather than only in the prompt because on one provider it is a bound and
   * "search at most six times" is a request.
   */
  maxSearches: number
  /**
   * Pages this pass may open.
   *
   * Anthropic bills opening separately and caps it. OpenAI folds opening into the
   * same tool with no separate cap, so there this number is read by NOTHING in
   * the request and reaches the model only as a request in the prompt. Said
   * plainly rather than dressed up as a translation.
   */
  maxFetches: number
  /**
   * Tokens of fetched TEXT this pass will accept from any one page, or absent for
   * no bound.
   *
   * Anthropic-only: it becomes `max_content_tokens` on the fetch tool. OpenAI
   * folds opening into its search tool and offers no equivalent, so there this is
   * read by nothing — said plainly rather than dressed up as a translation, the
   * same way `maxFetches` says it.
   *
   * ABSENT is the shipped state and the safe one. The provider truncates silently
   * — a cut result is byte-identical in shape to a whole one — so a bound is only
   * safe alongside the inference in `harvest`, and the SIZE of a safe bound is a
   * measurement nobody has made. See D52 and D53. It exists here so the
   * evaluation harness can set one without the app doing so.
   */
  maxContentTokens?: number
}

/**
 * A source the search actually returned.
 *
 * The citation-honesty invariant is enforced against this set and nothing else. A
 * URL the model names that is not in here never becomes a citation.
 */
export interface SeenSource {
  url: string
  /**
   * From the API response, NEVER from the model — on both providers.
   *
   * On Anthropic every search result carries one. On OpenAI a search action's
   * sources are `{ type: 'url', url }` with no title, so a title arrives only if
   * a `url_citation` annotation names that URL; otherwise the URL stands in. The
   * URL standing in is honest — it is not a claim about the page's content. A
   * model-written title is still ignored, and that rule does not relax.
   */
  title: string
  /**
   * `fetch` means the model OPENED the page: `web_fetch` on Anthropic,
   * `action.type === 'open_page'` on OpenAI.
   *
   * TWO values, deliberately. A third (`'opened'`) was considered and rejected: it
   * costs two disk-side gates in `persist.ts` and would demote every stored
   * OpenAI refutation on the first reload if either half slipped. The evidence
   * difference is carried by `Verification.quotesCheckable`, which gates the
   * WORDS — "read in full" never prints for a page whose text never came back.
   */
  via: 'search' | 'fetch'
  /**
   * The fetch tool's own text was, so far as this app can tell, CUT — see
   * `TRUNCATION_FLOOR` in the Anthropic adapter.
   *
   * Inferred and never reported: the provider documents truncation as a
   * behaviour and returns no flag, no length and no marker, so the only evidence
   * available is that the text came back at the size of the bound this app asked
   * for. Absent whenever no bound was set, which is every shipped pass today.
   *
   * What it is FOR is one line in `reconcile`: a quote not found on a page that
   * was cut is a check that could not be made, not a quote that is not there.
   */
  truncated?: boolean
  /**
   * The opened document's plain text, for the duration of the pass only.
   *
   * Set only where the provider returns it — and on Anthropic only when the
   * document arrived as text, so a fetched PDF gets nothing. On OpenAI it is
   * NEVER set, and that single absence is the whole of the port: `reconcile()`
   * needs no provider branch, because its tri-state `quoteFound` already means
   * "could not be checked".
   *
   * It must be `undefined` and never `''`: `fold('').includes(fold(quote))` is
   * `false`, which would mark every quote as fabricated — the citation gate turned
   * on its own user.
   */
  text?: string
}

/**
 * One provider-reported search action and only the sources attributed to it.
 *
 * The aggregate `SearchOutcome.sources` remains the citation gate. This parallel
 * ledger exists for coverage accounting: copying the aggregate set onto every
 * query would manufacture marginal yield, while flattening every action into one
 * row loses the order in which a search expanded.
 */
export interface SearchActionRound {
  /** Provider call/tool-use identity, stable inside this response. */
  id: string
  /** One action may issue a provider-defined batch of related query strings. */
  queries: string[]
  /** References the same source records held by the aggregate where possible. */
  sources: SeenSource[]
  completion: { kind: 'complete' } | { kind: 'failed'; code: SearchFailureCode; reason: string }
}

/**
 * The app's failure vocabulary, CLOSED.
 *
 * It used to be the API's `error_code` raw, which cannot survive two APIs. Each
 * adapter maps into these: Anthropic's eight arrive unchanged (they were already
 * the de-facto vocabulary), and OpenAI contributes `sources_not_returned` and
 * reuses `unavailable` and `url_not_accessible`.
 */
export type SearchFailureCode =
  | 'max_uses_exceeded'
  | 'too_many_requests'
  | 'unavailable'
  | 'query_too_long'
  | 'invalid_tool_input'
  | 'url_not_allowed'
  | 'url_not_accessible'
  | 'unsupported_content_type'
  /** The search ran but the API did not report which pages it returned, so the
   *  citation gate had nothing to admit from. OpenAI only; see its adapter. */
  | 'sources_not_returned'
  | 'unknown'

export interface SearchFailure {
  tool: 'search' | 'fetch'
  code: SearchFailureCode
}

/**
 * What the search pass actually did.
 *
 * Every field is a count of something that happened or a fact about the transport,
 * never a judgement. A "nothing found" reading is only sound if `failures` is
 * empty and `exhausted` is false, so both travel with the sources rather than
 * being reconstructed later.
 */
export interface SearchOutcome {
  sources: SeenSource[]
  queries: string[]
  /**
   * Exact provider action grouping where the adapter could observe it.
   *
   * Optional because older/custom adapters expose only the aggregate. Consumers
   * must retain that aggregate fallback rather than guessing an attribution.
   */
  rounds?: SearchActionRound[]
  failures: SearchFailure[]
  /**
   * Whether this provider's search returned an opened page's own text.
   *
   * Reported by the ADAPTER, not read from the registry: the adapter is what did
   * or did not receive it. `Verification.quotesCheckable` is written from this and
   * from nothing else — so a `preferCorpus` pass, which declares no tools and has
   * no `SearchOutcome` at all, records nothing rather than claiming a capability
   * it never exercised.
   */
  pageText: boolean
  /**
   * True when the gather was cut off rather than finished.
   *
   * Detected differently and meaning the same thing: on Anthropic the continuation
   * cap was reached with the tool loop still paused; on OpenAI the response came
   * back incomplete. `Verification.partial` reads it and says "read this as a
   * partial look rather than a finished check", which is true either way.
   */
  exhausted: boolean
}

/** Live search activity, for the ticker. Two variants because two are rendered. */
export type SearchEvent = { kind: 'query'; text: string } | { kind: 'open'; url: string }

/**
 * How many times a request is retried when nothing in the app says otherwise.
 *
 * Two, which is what both SDKs default to and what every call from `src/` makes:
 * a rate limit or a 503 on a long pass is worth one more attempt before a run
 * gives up a move. Named here rather than written twice in the adapters so that
 * `retriesFor` has one default to read and a test has one number to pin. The
 * adapters make these retries themselves now, not the SDKs — see `nextAttempt`.
 */
export const DEFAULT_RETRIES = 2

/** The retry policy for one request: what the caller asked for, or the app's own
 *  default. Shared by both adapters so neither can drift from the other. */
export const retriesFor = (o: { maxRetries?: number }): number => o.maxRetries ?? DEFAULT_RETRIES

/* ── asking again ──────────────────────────────────────────────────────────
   The SDKs used to make these retries themselves, out of sight, and two things
   followed from that. The wait they took was not a wait anything could stop: the
   SDK sleeps on a plain timer, so a Stop pressed while it honoured a `retry-after`
   held the lock for the rest of it — measured at 17 s of a 20 s wait, and up to a
   minute is legal. And nothing could say it was waiting: the live pass read
   "Reading the branch from the root down…" over a request the API had turned away.

   So both clients are built with no retries of their own, and each adapter asks
   again itself, on the SDKs' own terms, unchanged — the same number of tries
   (`retriesFor`), the same failures, the same waits — through `nextAttempt` and
   `pause`. What changed is only that the wait answers to the signal and says so.
   A request is asked again only when it failed BEFORE any of its response was
   read, which is also exactly when the SDKs did: nothing streamed, nothing billed
   for it, nothing landed. A failure after the first event is never retried by
   anything, here or in the SDK, and never was. */

/** What a turned-away request met, in the four words a reader is given for it. */
export type WaitCause = 'rate' | 'overloaded' | 'server' | 'network'

/** A wait the adapter is making before it asks again, reported once a second. */
export interface RetryWait {
  cause: WaitCause
  /** Left before it asks again; 0 as it asks. */
  ms: number
  /** The try about to be made, the first counted as 1 — so 2 or 3 by default. */
  attempt: number
  /** Tries in all, the first included. */
  of: number
}

/** The facts of one failed try, read off the SDK's error by the adapter that
 *  threw it — the only part of this that knows an SDK's classes. */
export interface AttemptFailure {
  status?: number
  headers?: { get(name: string): string | null } | null
  /** No response at all: the SDK's connection error. */
  connection: boolean
  /** The API said it was overloaded (Anthropic's 529 and `overloaded_error`). */
  overloaded?: boolean
}

/**
 * What the API asked for before the next request, in milliseconds, or null where
 * it did not say. `retry-after-ms` first, then `retry-after` as seconds or as an
 * HTTP date — the SDKs' own reading, in their order.
 */
export function retryAfterMs(headers: AttemptFailure['headers']): number | null {
  const ms = Number.parseFloat(headers?.get('retry-after-ms') ?? '')
  if (Number.isFinite(ms)) return ms
  const raw = headers?.get('retry-after')
  if (!raw) return null
  const sec = Number.parseFloat(raw)
  if (Number.isFinite(sec)) return sec * 1000
  const at = Date.parse(raw)
  return Number.isFinite(at) ? at - Date.now() : null
}

/**
 * Whether to ask again after this failure, and after how long — or null.
 *
 * The SDKs' terms, copied rather than invented: `x-should-retry` decides when it
 * is present; otherwise a 408, 409, 429, any 5xx and a connection that never
 * answered are asked again, while the try count lasts. The wait is the API's own
 * `retry-after` when it names one under a minute, and otherwise half a second
 * doubling per try, capped at eight, less up to a quarter at random. `used` is
 * the retries already made.
 */
export function nextAttempt(
  f: AttemptFailure,
  used: number,
  retries: number,
  rand: () => number = Math.random,
): { ms: number; cause: WaitCause } | null {
  if (used >= retries) return null
  const should = f.headers?.get('x-should-retry')
  const status = f.status
  const retryable =
    should === 'true'
      ? true
      : should === 'false'
        ? false
        : f.connection ||
          status === 408 ||
          status === 409 ||
          status === 429 ||
          (status !== undefined && status >= 500)
  if (!retryable) return null
  const cause: WaitCause =
    status === 429
      ? 'rate'
      : f.overloaded || status === 529
        ? 'overloaded'
        : status === undefined && f.connection
          ? 'network'
          : 'server'
  const asked = retryAfterMs(f.headers)
  const ms =
    asked !== null && asked > 0 && asked < 60_000
      ? asked
      : Math.min(0.5 * 2 ** used, 8) * (1 - rand() * 0.25) * 1000
  return { ms, cause }
}

/**
 * Wait, and stop waiting the moment the signal fires.
 *
 * Resolves either way; the caller reads the signal afterwards and throws its own
 * cancellation, so a Stop during a wait ends the pass the way a Stop during a
 * stream does. `tick` hears the time left once a second, for the line that says
 * what it is waiting for.
 */
export function pause(
  ms: number,
  signal?: AbortSignal,
  tick?: (left: number) => void,
): Promise<void> {
  return new Promise((resolve) => {
    if (signal?.aborted || ms <= 0) {
      resolve()
      return
    }
    const end = Date.now() + ms
    const done = () => {
      clearTimeout(timer)
      if (beat !== undefined) clearInterval(beat)
      signal?.removeEventListener('abort', done)
      resolve()
    }
    const timer = setTimeout(done, ms)
    const beat = tick ? setInterval(() => tick(Math.max(0, end - Date.now())), 1000) : undefined
    signal?.addEventListener('abort', done, { once: true })
  })
}

/** Say that a request is waiting to be asked again, then wait — for as long as
 *  the terms say, or until the signal fires. Shared by both adapters, so the two
 *  report a wait in one shape: at its start, once a second, and at zero. */
export async function waitToAsk(
  o: { signal?: AbortSignal; onWait?: (w: RetryWait) => void },
  w: { ms: number; cause: WaitCause },
  attempt: number,
  of: number,
): Promise<void> {
  const report = (ms: number) => o.onWait?.({ cause: w.cause, ms, attempt, of })
  report(w.ms)
  await pause(w.ms, o.signal, report)
  if (!o.signal?.aborted) report(0)
}

/**
 * The adapter's sentence for a request that failed on the API's side or the
 * wire's, from the facts and never from an SDK's message.
 *
 * Two facts decide the shape. `tries`: a sentence that says "turned away" after
 * three tries over several seconds is a different finding from one bounce, and
 * the reader deciding whether to wait deserves to know which. `began`: whether
 * any of the answer had been read — "could not reach the API" over a stream that
 * delivered two moves and then dropped sent the reader to inspect a firewall
 * that had let the whole answer through but its tail. What each ends with is the
 * thing to do, in words that fit a button pressed again and a run that carries
 * on alike.
 */
export function transportMessage(
  cause: WaitCause | 'garbled',
  f: { tries: number; began: boolean; status?: number; retryAfterMs?: number | null; host: string },
): string {
  const on = f.tries > 1 ? ` on all ${f.tries} tries` : ''
  const asked =
    f.retryAfterMs && f.retryAfterMs > 0
      ? ` It asked for ${Math.ceil(f.retryAfterMs / 1000)} s before the next request.`
      : ''
  switch (cause) {
    case 'rate':
      return f.began
        ? 'Rate limited partway through the answer. Wait a minute, then try again.'
        : `Rate limited: the API turned this request away${on}.${asked} Wait a minute, then try again.`
    case 'overloaded':
      return f.began
        ? 'The API broke off partway through the answer: it reported being overloaded. Try again in a few minutes.'
        : `The API is overloaded right now: it turned this request away${on}.${asked} Try again in a few minutes.`
    case 'server':
      return f.began
        ? 'The API broke off partway through the answer with a server error. Try again shortly.'
        : `The API returned a server error${f.status ? ` (${f.status})` : ''}${on}. Try again shortly.`
    case 'network':
      if (f.began)
        return 'The connection dropped partway through the answer. Check your connection, then try again.'
      // The page's own policy refused it (D336): the network was never asked, and
      // "check your connection" would send the reader to the wrong machine.
      if (cspRefused(f.host)) return cspRefusedSentence(f.host)
      return `Could not reach the API${on}. Check your connection — and note that some corporate networks block direct browser calls to ${f.host}.`
    case 'garbled':
      return 'Part of the answer arrived garbled, so the rest of it could not be read. Try again.'
  }
}

export interface StreamOptions {
  /** Replaces the old bare `apiKey`. With two providers a key does not say which
   *  API to send it to, and a key sent to the wrong host has already leaked. */
  model: ResolvedModel
  /** Neutral as a string. How it is CACHED is not, and is not abstracted. */
  system: string
  user: string
  schema: SchemaSpec
  /**
   * Selects one direct array property from a structured root object for live
   * object emission. Absent preserves the historical first-array behavior.
   */
  rootArrayKey?: string
  /** What the caller ASKS for. The dispatcher lowers it to what the model
   *  accepts, in one place, and nothing rounds up. */
  effort: Effort
  /** Clamped to `entry.maxOutput` by the dispatcher when that is known. */
  maxTokens?: number
  signal?: AbortSignal
  onObject: (obj: unknown) => void
  /**
   * A contract, not a hint: consecutive summary blocks arrive with no separator on
   * BOTH providers, so each adapter emits `'\n\n'` at a block boundary
   * (`content_block_start` on Anthropic, a change of `item_id#summary_index` on
   * OpenAI). The two-newline convention is shared; the event that detects it is
   * not shareable and is not shared.
   */
  onThinking: (delta: string) => void
  /**
   * Called at most once, on the first stream event that shows the provider has
   * read the whole prompt: Anthropic's `message_start`, OpenAI's first event past
   * `response.created`/`in_progress`/`queued`. Retries and continuation turns do
   * not call it again.
   *
   * It exists for the panel (`runPanel`), and for one fact about prompt caching:
   * an entry is readable only once a response has begun, so a second request
   * sent with the same prefix before then pays for the whole prefix again. The
   * panel starts its other two seats here — together, and with the prefix now
   * cached — rather than after the first seat has finished.
   */
  onOpen?: () => void
  /**
   * Absent for every reasoning op.
   *
   * Declaring tools on a pass that will not use them costs nothing in requests
   * and everything in cache. On Anthropic `tools` render before `system` and this
   * app's one `cache_control` sits on the last system block, so a call that
   * declares them can never share a cached prefix with one that does not. On
   * OpenAI caching is automatic, which makes a poisoned prefix invisible in the
   * response rather than absent — the same rule, harder to notice breaking.
   */
  web?: WebOptions
  onSearch?: (ev: SearchEvent) => void
  /**
   * A routing hint for prompt caching. NEVER an identity.
   *
   * Read only by the OpenAI adapter, which has no explicit breakpoint and takes
   * `prompt_cache_key`; the Anthropic adapter already puts its one `cache_control`
   * on the last system block. It is on the seam rather than guessed inside an
   * adapter because the only call that needs it is the panel, whose three seats
   * are byte-identical by design — app structure an adapter cannot see.
   */
  cacheKey?: string
  /**
   * How many times the adapter may retry a transient failure before any of the
   * response was read — on the SDKs' terms, which it keeps itself now so the
   * wait is said (`onWait`) and a Stop ends it (`nextAttempt`).
   *
   * Absent is `DEFAULT_RETRIES`, which is what the app runs and what every call
   * in `src/` gets. `0` exists for the frozen evaluation in `evals/`, whose
   * deterministic gates include *no extra model substitution or retry*: a retry
   * is a second attempt that no record can show — neither the tokens it drew nor
   * the fact that the first one failed — so an arm's transient unreliability
   * would be absorbed by the transport instead of measured by the experiment.
   * Setting it to zero turns an invisible second attempt into a visible failure,
   * which is the trade that harness makes everywhere else.
   *
   * It is on the seam rather than read from an environment variable inside an
   * adapter, because a retry policy that a caller cannot see is the thing being
   * fixed here.
   */
  maxRetries?: number
  /**
   * Called while the adapter waits before asking again — once as the wait
   * starts, once a second through it, and with `ms: 0` as it asks. Never called
   * for a request that is not asked again. See `nextAttempt`.
   */
  onWait?: (w: RetryWait) => void
}

export interface StreamResult {
  objects: unknown[]
  /**
   * The complete structured root object, available only when `rootArrayKey` was
   * requested and the whole response parsed. Kept in memory for sibling fields
   * such as portfolio target detail; adapters never log or send it to telemetry.
   */
  envelope?: Record<string, unknown>
  /**
   * What this pass cost and which model billed it.
   *
   * `spend.model` is minted from the RESPONSE's model id, so a substitution would
   * show up as an extra ledger row rather than as a silent mis-attribution.
   */
  spend: Spend
  refused: boolean
  refusalNote?: string
  /**
   * The safety filter's category, where the provider names one — Anthropic's
   * `stop_details.category` ("bio", "cyber", …), an open set. Absent everywhere
   * else, and on a refusal the provider did not categorise (D312).
   */
  refusalCategory?: string
  truncated: boolean
  /**
   * Present if and only if server tools were declared.
   *
   * `undefined` and `{ sources: [] }` are different findings, and the difference
   * is the whole of the prior-art half: one means nobody looked, the other means
   * somebody looked and found nothing. An optional field is the only shape that
   * cannot conflate them.
   */
  search?: SearchOutcome
}

/* ── prose, rather than a schema ───────────────────────────────────────────
   A second, much smaller shape on the same seam. It exists because a
   conversation is prose: forcing it through the object extractor would mean a
   schema nobody reads, an answer that cannot stream a sentence at a time, and a
   JSON envelope around text whose whole value is being text.

   Note what is NOT here, and it is deliberate: no `web`, and no `onSearch`. A
   conversation cannot search. An answer carrying model-written citations would
   walk straight past the citation gate that every source check in this app is
   built on — the gate admits a URL only if the search API returned it, and a chat
   has no search API behind it to return anything. So a conversation is the model's
   own reading, and every surface that shows one says exactly that. Wanting a
   source is what the source check is for. */

export interface TextTurn {
  role: 'user' | 'assistant'
  text: string
}

export interface TextOptions {
  model: ResolvedModel
  /**
   * The stable head of the system prompt: the contract, the frame and the
   * standing brief. This is the block the Anthropic adapter marks with its first
   * system `cache_control`, so everything in it must be the same bytes from one
   * call to the next or the marker buys nothing.
   */
  system: string
  /**
   * A second marked block, rendered between `system` and `systemTail`, for
   * content that is stable across the TURNS of one conversation and changes only
   * when the graph under it does: the node or graph brief the ask channel
   * discusses. `ask` recomputes that brief from the live graph on every turn, so
   * a pass landing between two turns — the D51 flow — changes it; inside
   * `system` that re-wrote the whole head on exactly the turn the split existed
   * to spare, and in the unmarked tail it would be full-price input on every
   * turn of a conversation in which nothing changed, which is most of them. The
   * provider checks for a hit at every earlier breakpoint, so its own marker is
   * what lets the head read while this block alone is written. The OpenAI
   * adapter joins it onto the instructions in order, with the same two newlines
   * the callers used to put there, and the model reads the same words either way.
   */
  systemDynamic?: string
  /**
   * The volatile half, rendered AFTER `system` and never marked for caching.
   *
   * It exists for one shape of content: the id table the acts protocol quotes
   * from, which lists every node on the graph and therefore changes every time a
   * pass lands. That table used to ride inside `system`, under the marker — and
   * a marker on a prefix that changes on every call is a 1.25× write with no
   * read to follow it, on a call whose whole point is to fire after a pass has
   * just changed the graph. Splitting the two is a billing change and nothing
   * else: the Anthropic adapter sends this as a second system block, the
   * OpenAI adapter joins it back onto `instructions` with the same two newlines
   * the callers used to put there, and the model reads the same words either way.
   */
  systemTail?: string
  user: string
  /** Prior turns, oldest first. Sent verbatim, so a conversation stays a
   *  conversation rather than a series of unrelated questions. */
  history?: TextTurn[]
  effort: Effort
  maxTokens?: number
  signal?: AbortSignal
  /** Called per delta. A conversation that arrives all at once reads as a page
   *  load, exactly as a branch that does. */
  onText: (delta: string) => void
  cacheKey?: string
  /** Same explicit retry control as structured calls, including evaluation. */
  maxRetries?: number
  /** The same waits as a structured call's — see `StreamOptions.onWait`. */
  onWait?: (w: RetryWait) => void
}

/**
 * The three system tiers as ONE string, in the documented order.
 *
 * For every adapter whose API takes a single system field: the split above is
 * an Anthropic cache-marker arrangement, and where there are no markers to
 * place there is nothing to preserve — the model reads the same words either
 * way, joined with the same two newlines the callers used to put between them.
 *
 * It lives here rather than in one adapter because three of them need it and a
 * second copy of the ORDER is a second thing to keep in step. The two that
 * carried their own dropped `systemDynamic` and `systemTail` on the ask path
 * and sent the contract alone: the model was told how to answer a question
 * about a claim and never told which claim.
 */
export function systemTiers(
  o: Pick<TextOptions, 'system' | 'systemDynamic' | 'systemTail'>,
): string {
  return [o.system, o.systemDynamic, o.systemTail].filter((part) => part).join('\n\n')
}

/**
 * A stream that ended without its terminal marker, and nobody pressed Stop.
 *
 * Every streaming adapter here knows when a response is OVER: Google sets a
 * `finishReason` on the last candidate, the Responses API sends
 * `response.completed` or `response.incomplete`, Chat Completions sets
 * `finish_reason` on its final chunk, and the Anthropic SDK makes it structural
 * by awaiting `finalMessage()` — which is why that adapter needs nothing here.
 * The other four checked the marker only to tell an ABORT from an empty answer,
 * so a body that closed early for any other reason — a proxy cutting the
 * response, a gateway dying mid-answer — returned what had arrived so far as a
 * complete, untruncated pass.
 *
 * That is the failure this whole app is built against: half an answer presented
 * as a whole one, with nothing on the card to say so. A pass that produced two
 * branches out of five reads as a branch that ran out of ideas, and a source
 * check cut off after one result reads as a claim with one source.
 *
 * `network`, deliberately, because that is what it is and because `endsTheRun`
 * lets a run survive one: a cut connection is a failed MOVE, not a dead
 * account. The marker's own name is in the message so somebody pointing this
 * app at a gateway of their own can see precisely what it did not send.
 *
 * It says nothing about what was recorded, because it cannot know: the partial
 * nodes that arrived before the cut have already landed through `onObject`, and
 * `failureSentence` in `core/failure.ts` appends what stayed from the record's
 * own count (D219). It once said "Nothing was recorded." here, so the toast read
 * that and, one sentence later, named the nodes that stayed on the graph (D342).
 */
export function unterminated(marker: string, spent?: Spend): ModelError {
  return new ModelError(
    `The model's response ended early — the stream closed before ${marker} arrived, so what came back is part of an answer rather than a short one. Try the pass again.`,
    'network',
    spent,
  )
}

export interface TextResult {
  text: string
  spend: Spend
  refused: boolean
  refusalNote?: string
  /** As `StreamResult.refusalCategory`. */
  refusalCategory?: string
  truncated: boolean
}

export class ModelError extends Error {
  constructor(
    message: string,
    /** `timeout` is minted in exactly one place — the transport's per-pass
     *  deadline — and exists to be distinguishable from `aborted`: both are the
     *  same signal firing, but one is the user's hand and the other is a hung
     *  request, and `reason.ts` reads `aborted` as "Stopped." at nine call sites.
     *  A timeout that stayed `aborted` would be recorded as the user cancelling a
     *  pass the network killed. */
    readonly kind:
      | 'storage'
      | 'auth'
      | 'rate'
      /** The account is out of credits. Separate from `rate` because waiting does
       *  not help and a run must stop at once rather than spend its remaining
       *  moves on requests that cannot succeed. */
      | 'quota'
      | 'network'
      | 'server'
      | 'request'
      | 'aborted'
      | 'timeout'
      | 'unknown',
    /**
     * What the API had already billed, and WHICH MODEL billed it.
     *
     * The model is why this changed shape: cancelling a pass mid-search does not
     * un-bill the searches, and with two models in a session an unattributed token
     * count cannot be added to either row of the ledger without making both wrong.
     */
    readonly spent?: Spend,
  ) {
    super(message)
    this.name = 'ModelError'
  }
}

/** One line per code, written rather than templated, because "the search failed"
 *  and "nothing came back" are the same empty list to the code and opposite
 *  findings to a reader. Exhaustive, so there is no live default. */
export const SEARCH_FAILURE_NOTE: Record<SearchFailureCode, string> = {
  max_uses_exceeded: 'The pass used its whole search budget.',
  too_many_requests: 'The search service was rate limiting.',
  unavailable: 'The search service did not answer.',
  query_too_long: 'One query was too long to run.',
  invalid_tool_input: 'One search was malformed and did not run.',
  url_not_allowed: 'A page could not be opened.',
  url_not_accessible: 'A page could not be opened.',
  unsupported_content_type: 'A page was in a format that could not be read.',
  sources_not_returned: 'The search ran but did not report which pages it returned.',
  unknown: 'A search step did not complete.',
}

export const failureNote = (code: SearchFailureCode): string => SEARCH_FAILURE_NOTE[code]

/**
 * The citation-honesty gate. A URL the search never returned is not a citation,
 * whatever the model calls it.
 *
 * MUST NEVER be applied to a passage citation from the user's own documents. A
 * document-grounded pass declares no server tools, so `seen` is empty and this
 * would drop every citation the model made — the feature would look as though the
 * model had refused to cite. Passages are validated against the printed retrieval
 * table instead, and the two gates share no code path.
 *
 * One gate, one canonicaliser, two harvesters: the note in `core/url.ts` about two
 * canonicalisations being how the gate leaks now covers two providers.
 */
export function matchSeen(url: string, seen: SeenSource[]): SeenSource | null {
  const key = canonicalUrl(url)
  for (const s of seen) if (canonicalUrl(s.url) === key) return s
  return null
}

/** Re-exported so an adapter needs one import for the whole seam. */
export type { ModelStamp, Spend, SpendDetail }
